import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import HouseIcon from '@/assets/property-icons/house-icon.svg'
import BedIcon from'@/assets/property-icons/bed-icon.svg'
import BathIcon from '@/assets/property-icons/bath-icon.svg'
import SquareIcon from '@/assets/property-icons/square-icon.svg'
import AddressPoint from '@/assets/property-icons/address-point-icon.svg'
import { UnityForListing } from "@/custom-types";
import { FileTypeOption, UnityStatusOption, useGetPropertyLazyQuery } from "@/graphql/generated";

type UnityDetails = {
  bedrooms: number
  bathrooms: number
  size: number
}

type PropertyInfoMiniCardProps = {
  propertyId: string;
  name: string
  address: string
  image?: string
  unityDetails?: UnityDetails
  unities?: UnityForListing[];
}

const CardContainer = styled.div`
  width: 280px;
  border-radius: 12px;
  overflow: visible;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  background-color: #fff;
  display: flex;
  flex-direction: column;
  margin: 16px;
  position: relative;
  padding-top: 120px;
  transition: transform, 0.2s;
  &:hover {
    transform: scale(1.05);
  }
`;

const ImageContainer = styled.div`
  width: 246px;
  height: 160px;
  overflow: hidden;
  position: absolute;
  top: -50px;
  left: 50%;
  transform: translateX(-50%);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: 16px;

  
`;

const PropertyImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 16px;
`;

const CardContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BuildingName = styled.h1`
  font-size: 20px;
  font-weight: bold;
  margin: 0;
  color: black;
  text-align: left;
`;

const Location = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: darkgray;
  text-align: left;
  justify-content: flex-start;
  padding-bottom: 12px;
  position: relative;

  &::after {
    content: "";
    display: block;
    width: 70%;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.1);
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
  }

  svg {
    margin-right: 8px;
    color: #4caf50;
  }
`;

const Features = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: darkgray;

  div {
    display: flex;
    align-items: center;

    svg {
      margin-right: 4px;
      color: darkgray;
    }
  }
`;

const IconContainer = styled.div`
  background-color: gray;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 360px;
  max-height: 250px;

`

const PicturePlaceHolder = ({ imageSrc }: {imageSrc: string}) => (
  <IconContainer>
    <img src={imageSrc} alt="" />
  </IconContainer>
)

const UnitiesCounter = ({unities}: {unities: UnityForListing[]}) => {
  const unitiesTotal = unities.length
  const availableUnities = unities.filter((unity) => unity.currentStatusOption === UnityStatusOption.Vacant).length

  return (
    <div style={{ display: 'flex', gap: '12px', flexDirection: 'column', color: 'gray' }}>
      <div>Unities:</div>
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-around'}}>
        <div>
          {`total: ${unitiesTotal}`}
        </div>
        <div>
          {`available: ${availableUnities}`}
        </div>
      </div>
    </div>
  )
}

export const PropertyInfoMiniCard = ({
  propertyId,
  image,
  name,
  address,
  unityDetails,
  unities,
}: PropertyInfoMiniCardProps) => {
  const [imageUrl, setImageUrl] = useState<string | undefined>(image);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const [loadProperty, { data }] = useGetPropertyLazyQuery();

  useEffect(() => {
    if (!propertyId) return;

    const node = cardRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          loadProperty({
            variables: { id: propertyId },
          });
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [loadProperty, propertyId]);

  useEffect(() => {
    if (!data?.property || imageUrl) return;

    const firstPicture = data.property.fileUploads
      ?.filter((file) => file.fileTypeOption === FileTypeOption.Picture && file.urlToDownload)
      ?.at(0);

    if (firstPicture?.urlToDownload) {
      setImageUrl(firstPicture.urlToDownload as string);
    }
  }, [data, imageUrl]);

  return (
    <CardContainer ref={cardRef}>
      <ImageContainer>
        {
          imageUrl
          ? <PropertyImage src={imageUrl} alt="" />
          : <PicturePlaceHolder imageSrc={HouseIcon}/>
        }
      </ImageContainer>
      <CardContent>
        <BuildingName>{name}</BuildingName>
        <Location>
          <img src={AddressPoint} alt="" />
          &nbsp;
          <span>{address}</span>
        </Location>
        {
          unityDetails
          ? (
            <Features>
              <div>
                <img src={BedIcon} alt="" />
                <span>{unityDetails.bedrooms}</span>
              </div>
              <div>
                <img src={BathIcon} alt="" />
                <span>{unityDetails.bathrooms}</span>
              </div>
                <img src={SquareIcon} alt="" />
                <span>{`${unityDetails.size} SQFT`}</span>
            </Features>
          )
          : <UnitiesCounter unities={unities ?? []} />
        }
      </CardContent>
    </CardContainer>
  );
};
